import {
  buildAdminApplicationPath,
  getAdminApplication,
  type AdminApplicationId,
} from '../apps/adminApplications';

export type AdminNavNotificationKey = 'metrics' | 'support';

export type AdminNavItem = {
  path: string;
  label: string;
  notificationKey?: AdminNavNotificationKey;
  scope?: 'application' | 'global';
};

export type AdminNavNotificationCounts = Partial<Record<AdminNavNotificationKey, number>>;

export const ADMIN_HOME_PATH = buildAdminApplicationPath('sajug', 'accounts');

export const GLOBAL_ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { path: '/content', label: 'CMS' },
  { path: '/reports', label: 'Reports' },
  { path: '/user-reports', label: '신고' },
  { path: '/benefit-campaign', label: '혜택' },
  { path: '/support', label: 'Support', notificationKey: 'support' },
  { path: '/site-visits', label: '사이트 방문' },
  { path: '/books', label: '책방' },
  { path: '/reviews', label: '리뷰' },
  { path: '/announcements', label: '공지' },
];

export function getAdminNavItems(applicationId: AdminApplicationId): AdminNavItem[] {
  const application = getAdminApplication(applicationId);
  const appItems: AdminNavItem[] = [
    { path: buildAdminApplicationPath(applicationId, 'accounts'), label: 'Accounts', scope: 'application' },
  ];
  if (application.capabilities.coupons) {
    appItems.push({ path: buildAdminApplicationPath(applicationId, 'coupons'), label: 'Coupons', scope: 'application' });
  }
  appItems.push({
    path: buildAdminApplicationPath(applicationId, 'metrics'),
    label: 'Metrics',
    notificationKey: 'metrics',
    scope: 'application',
  });
  if (applicationId === 'saju-ai') {
    appItems.push({ path: buildAdminApplicationPath(applicationId, 'usage-credits'), label: '사용량·닢', scope: 'application' });
  }
  if (application.capabilities.ugotPro) {
    appItems.push({ path: buildAdminApplicationPath(applicationId, 'ugot-pro'), label: 'UGOT Pro', scope: 'application' });
  }
  if (application.capabilities.internalTesters) {
    appItems.push({
      path: buildAdminApplicationPath(applicationId, 'internal-testers'),
      label: '내부테스터',
      scope: 'application',
    });
  }
  return [...appItems, ...GLOBAL_ADMIN_NAV_ITEMS.map((item) => ({ ...item, scope: 'global' as const }))];
}

export const ADMIN_NAV_ITEMS = getAdminNavItems('sajug');

export function isAdminNavItemActive(item: AdminNavItem, pathname: string): boolean {
  if (item.path === '/') {
    return pathname === '/';
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

export function getAdminNavItemNotificationCount(
  item: AdminNavItem,
  counts: AdminNavNotificationCounts,
): number {
  if (!item.notificationKey) {
    return 0;
  }
  const value = counts[item.notificationKey];
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

export function formatAdminNavNotificationCount(count: number): string {
  return count > 99 ? '99+' : `+${count}`;
}
